import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Loader2,
  AlertTriangle,
  RotateCw,
  Send,
  Save,
  X,
  Pencil,
  Mail,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useLetterDraft } from './useLetterDraft.ts'
import { useKundenschreiberSetup } from './useKundenschreiberSetup.ts'

// Subset of the page-level Prospect type that's actually needed to compose
// and send a letter. Keep field names in sync with NeukundengewinnungPage.
export interface LetterComposerProspect {
  id: string
  companyName: string
  contactFirstName?: string | null
  contactLastName?: string | null
  street?: string | null
  zipCode?: string | null
  city?: string | null
  country?: string | null
}

export interface LetterComposerProps {
  prospect: LetterComposerProspect
  referenceCustomerId?: string | null
  /** Called after a successful Kundenschreiber send. */
  onSent?: () => void
  /** When true the composer is rendered read-only (no edit / send). */
  disabled?: boolean
  /**
   * Extra action buttons injected into the action row. Used by the daily-queue
   * carousel to add Reject / Skip buttons while the single-letter modal does
   * not need them.
   */
  extraActions?: React.ReactNode
  /** Styling variant. 'card' is the daily-queue layout, 'modal' is compact. */
  variant?: 'card' | 'modal'
}

export function LetterComposer({
  prospect,
  referenceCustomerId,
  onSent,
  disabled = false,
  extraActions,
  variant = 'card',
}: LetterComposerProps) {
  const queryClient = useQueryClient()
  const draft = useLetterDraft(prospect.id, referenceCustomerId)
  const ks = useKundenschreiberSetup()
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)

  async function handleSend() {
    if (!draft.letterText || sending || !ks.selectedTemplateId) return

    setSending(true)
    setSendError(null)
    try {
      const payload: Record<string, unknown> = {
        template_id: ks.selectedTemplateId,
        recipient: {
          first_name: prospect.contactFirstName || '',
          last_name: prospect.contactLastName || prospect.companyName,
          company_name: prospect.companyName,
          street: prospect.street || '',
          zip: prospect.zipCode || '',
          city: prospect.city || '',
          country: prospect.country === 'DE' ? 'Deutschland' : prospect.country || 'Deutschland',
        },
        personalization: {
          letter_text: draft.letterText,
        },
      }
      if (ks.selectedSenderId) payload.sender_id = ks.selectedSenderId
      if (draft.letterId) payload.letter_id = draft.letterId

      await apiClient.post('/shipping/kundenschreiber/send', payload)

      queryClient.invalidateQueries({ queryKey: ['kundenschreiber-balance'] })
      queryClient.invalidateQueries({ queryKey: ['prospect-letters', prospect.id] })
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })

      onSent?.()
    } catch (err: unknown) {
      const axErr = err as {
        response?: { data?: { message?: string; error?: string } }
        message?: string
      }
      const msg =
        axErr?.response?.data?.message ||
        axErr?.response?.data?.error ||
        axErr?.message ||
        'Unbekannter Fehler'
      setSendError(msg)
    } finally {
      setSending(false)
    }
  }

  const canEdit = !disabled
  const showConfidence = !draft.isGenerating && draft.letterText && draft.confidence !== null
  const selectedTemplate = (ks.templates ?? []).find((t) => t.id === ks.selectedTemplateId)
  const isModal = variant === 'modal'

  return (
    <div className={cn('flex flex-col', isModal ? 'gap-3' : 'gap-0')}>
      {/* Letter content area */}
      <div className={cn(isModal ? '' : 'px-6 py-5')}>
        {draft.isGenerating && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mb-3 text-brand-400" />
            <p className="text-sm font-medium">Brief wird generiert...</p>
            <p className="text-xs text-gray-300 mt-1">KI schreibt einen personalisierten Brief</p>
          </div>
        )}

        {!draft.isGenerating && draft.error && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center max-w-md">
              <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-red-700 mb-1">Briefgenerierung fehlgeschlagen</p>
              <p className="text-xs text-red-600">{draft.error}</p>
              <button
                onClick={draft.regenerate}
                className="mt-3 flex items-center gap-1.5 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium rounded-lg transition-colors mx-auto"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Erneut versuchen
              </button>
            </div>
          </div>
        )}

        {!draft.isGenerating && !draft.error && draft.letterText && (
          <div className="relative group">
            {showConfidence && (
              <div className="mb-2 flex justify-end">
                <span
                  className={cn(
                    'text-[11px] font-semibold px-2 py-0.5 rounded-full border',
                    draft.confidence! >= 90
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : draft.confidence! >= 70
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : draft.confidence! >= 50
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-red-50 border-red-200 text-red-700',
                  )}
                >
                  Sicherheit: {draft.confidence}%
                </span>
              </div>
            )}
            {draft.isEditing ? (
              <div className="bg-white border-2 border-brand-400 rounded-xl p-3 shadow-sm">
                <textarea
                  value={draft.editedText}
                  onChange={(e) => draft.setEditedText(e.target.value)}
                  autoFocus
                  rows={Math.max(8, draft.editedText.split('\n').length + 1)}
                  className="w-full text-sm text-gray-800 font-serif leading-relaxed p-2 bg-amber-50/50 border border-amber-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-y"
                  disabled={draft.savingDraft}
                />
                {draft.saveError && (
                  <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    {draft.saveError}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-gray-400">
                    Manuelle Änderungen werden im Entwurf gespeichert.
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={draft.cancelEditing}
                      disabled={draft.savingDraft}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      <X className="w-3.5 h-3.5" />
                      Abbrechen
                    </button>
                    <button
                      onClick={draft.saveDraft}
                      disabled={draft.savingDraft || draft.editedText.trim().length < 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-md disabled:opacity-50"
                    >
                      {draft.savingDraft ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      {draft.savingDraft ? 'Speichert...' : 'Speichern'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div
                  onDoubleClick={canEdit ? draft.startEditing : undefined}
                  title={canEdit ? 'Doppelklick zum Bearbeiten' : undefined}
                  className={cn(
                    'bg-amber-50/50 border border-amber-200/60 rounded-xl p-5 transition-colors',
                    canEdit && 'cursor-text hover:border-amber-300 hover:bg-amber-50/80',
                  )}
                >
                  <p className="text-sm text-gray-800 whitespace-pre-wrap font-serif leading-relaxed">
                    {draft.letterText}
                  </p>
                </div>
                {canEdit && (
                  <button
                    onClick={draft.startEditing}
                    title="Brief manuell bearbeiten"
                    className="absolute top-2 right-2 p-1.5 rounded-md bg-white/70 text-gray-400 hover:bg-white hover:text-brand-500 border border-transparent hover:border-amber-200 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
            {draft.warnings.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {draft.warnings.map((w, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700"
                  >
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    {w}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {!draft.isGenerating && !draft.error && !draft.letterText && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-300">
            <Mail className="w-8 h-8 mb-2" />
            <p className="text-sm">Brief wird geladen...</p>
          </div>
        )}
      </div>

      {/* Kundenschreiber settings */}
      {!disabled && draft.letterText && (
        <div
          className={cn(
            'bg-blue-50/50 border-blue-100/60',
            isModal ? 'rounded-lg border px-3 py-2' : 'px-6 py-3 border-t',
          )}
        >
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide whitespace-nowrap">
              Kundenschreiber
            </span>
            {ks.balance && (
              <span className="text-xs text-blue-600">{ks.balance.shipment_credits} Credits</span>
            )}
            <select
              value={ks.selectedTemplateId}
              onChange={(e) => ks.setSelectedTemplateId(e.target.value)}
              className="text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white text-gray-700 min-w-[180px]"
            >
              <option value="">Vorlage wählen...</option>
              {(ks.templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.variables?.length ? ` [${t.variables.join(', ')}]` : ''}
                </option>
              ))}
            </select>
            {selectedTemplate?.variables?.length ? (
              <span className="text-[10px] text-blue-500 font-mono">
                vars: {selectedTemplate.variables.join(', ')}
              </span>
            ) : null}
            <select
              value={ks.selectedSenderId}
              onChange={(e) => ks.setSelectedSenderId(e.target.value)}
              className="text-xs border border-blue-200 rounded-md px-2 py-1.5 bg-white text-gray-700 min-w-[160px]"
            >
              <option value="">Absender wählen...</option>
              {(ks.senders ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label ||
                    [s.first_name, s.last_name, s.company_name].filter(Boolean).join(' ') ||
                    s.id}
                </option>
              ))}
            </select>
            {ks.defaultsChanged && (
              <button
                onClick={ks.saveDefaults}
                disabled={ks.savingDefaults}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1.5 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50"
                title="Aktuelle Auswahl als Standard für diesen Mandanten speichern"
              >
                {ks.savingDefaults ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                Als Standard
              </button>
            )}
            {!ks.defaultsChanged && ks.defaults?.defaultTemplateId && ks.selectedTemplateId && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Check className="w-3 h-3" />
                Standard
              </span>
            )}
            {ks.ksTemplatesError ? (
              <div className="text-xs text-red-500">
                <span className="italic">
                  Vorlagen konnten nicht geladen werden
                  {ks.ksTemplateErrorMsg ? `: ${ks.ksTemplateErrorMsg}` : '.'}
                </span>
                {ks.ksDebug ? (
                  <details className="mt-1">
                    <summary className="cursor-pointer underline">Debug-Info</summary>
                    <pre className="mt-1 bg-red-50 p-2 rounded text-[10px] whitespace-pre-wrap max-w-[600px] overflow-auto">
                      {JSON.stringify(ks.ksDebug, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
            {!ks.ksTemplatesError &&
              !ks.selectedTemplateId &&
              Array.isArray(ks.templates) &&
              ks.templates.length === 0 && (
                <span className="text-xs text-orange-500 italic">
                  Keine Vorlagen im Kundenschreiber-Account vorhanden. Bitte zuerst eine Vorlage auf
                  kundenschreiber.de anlegen.
                </span>
              )}
            {!ks.ksTemplatesError &&
              !ks.selectedTemplateId &&
              (!Array.isArray(ks.templates) || ks.templates.length > 0) && (
                <span className="text-xs text-gray-400 italic">
                  Bitte Vorlage wählen um Brief zu versenden
                </span>
              )}
          </div>
        </div>
      )}

      {/* Action buttons bar */}
      {!disabled && (
        <div
          className={cn(
            isModal
              ? 'flex items-center gap-2 flex-wrap pt-1'
              : 'px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap',
          )}
        >
          {extraActions}

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={draft.regenerate}
              disabled={draft.isGenerating}
              className="flex items-center gap-1.5 px-4 py-2 border border-brand-200 text-brand-600 text-sm font-medium rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50"
              title="Brief neu generieren"
            >
              <RotateCw className={cn('w-4 h-4', draft.isGenerating && 'animate-spin')} />
              Neu generieren
            </button>

            <button
              onClick={handleSend}
              disabled={!draft.letterText || draft.isGenerating || sending || !ks.selectedTemplateId}
              className="flex items-center gap-1.5 px-5 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 shadow-sm"
              title={!ks.selectedTemplateId ? 'Bitte zuerst eine Vorlage wählen' : ''}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Wird gesendet...' : 'Abschicken'}
            </button>
          </div>
        </div>
      )}

      {sendError && (
        <div
          className={cn(
            'text-xs text-red-600 flex items-center gap-1',
            isModal ? '' : 'px-6 pb-3',
          )}
        >
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          Fehler beim Versand: {sendError}
        </div>
      )}
    </div>
  )
}
